import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { useDispatch } from 'react-redux'
import Swal from 'sweetalert2'
import { AsyncPaginate } from 'react-select-async-paginate'
import { addNotification, notificationOption } from '../../slice/notificationSlice'

// ─── Validation Schema ────────────────────────────────────────────────────────
const notificationSchema = yup.object().shape({
    title: yup
        .string()
        .required('Title is required')
        .min(2, 'Title must be at least 2 characters')
        .max(100, 'Title cannot exceed 100 characters'),

    message: yup
        .string()
        .required('Message is required')
        .min(5, 'Message must be at least 5 characters')
        .max(500, 'Message cannot exceed 500 characters'),

    title_ar: yup
        .string()
        .required('Title (Arabic) is required')
        .min(2, 'Title (Arabic) must be at least 2 characters')
        .max(100, 'Title (Arabic) cannot exceed 100 characters'),

    message_ar: yup
        .string()
        .required('Message (Arabic) is required')
        .min(5, 'Message (Arabic) must be at least 5 characters')
        .max(500, 'Message (Arabic) cannot exceed 500 characters'),

    selectedUsers: yup
        .array()
        .nullable()
        .test(
            'users-or-send-all',
            'Please select at least one user or enable Send All',
            function (value) {
                const { sendAll, sendAllTutors, sendAllParents } = this.parent
                if (sendAll || sendAllTutors || sendAllParents) return true
                return Array.isArray(value) && value.length > 0
            }
        ),
})

// ─── Custom Option ────────────────────────────────────────────────────────────
const CustomOption = ({ innerProps, innerRef, data, isFocused, isSelected }) => (
    <div
        ref={innerRef}
        {...innerProps}
        style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '8px 14px',
            cursor: 'pointer',
            backgroundColor: isSelected ? '#e8f0fe' : isFocused ? '#f5f8ff' : 'white',
            borderBottom: '1px solid #f1f3f5',
        }}
    >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{
                width: 32, height: 32, borderRadius: '50%',
                backgroundColor: data.role === 'tutor' ? '#dbeafe' : '#fce7f3',
                color: data.role === 'tutor' ? '#1d4ed8' : '#be185d',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 700, fontSize: 13, flexShrink: 0,
            }}>
                {data.label?.charAt(0)?.toUpperCase()}
            </div>
            <div>
                <div style={{ fontSize: 14, fontWeight: 500, color: '#212529', lineHeight: 1.3 }}>
                    {data.label}
                </div>
                {data.subLabel && (
                    <div style={{ fontSize: 12, color: '#6c757d', lineHeight: 1.3 }}>
                        {data.subLabel}
                    </div>
                )}
            </div>
        </div>
        <span style={{
            fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20,
            backgroundColor: data.role === 'tutor' ? '#dbeafe' : '#fce7f3',
            color: data.role === 'tutor' ? '#1d4ed8' : '#be185d',
            textTransform: 'capitalize', flexShrink: 0,
        }}>
            {data.role}
        </span>
    </div>
)

// ─── Custom MultiValue tag ────────────────────────────────────────────────────
const CustomMultiValue = ({ data, removeProps }) => (
    <div style={{
        display: 'flex', alignItems: 'center', gap: 4,
        backgroundColor: data.role === 'tutor' ? '#dbeafe' : '#fce7f3',
        borderRadius: 4, padding: '2px 6px 2px 8px', margin: '2px',
    }}>
        <span style={{
            fontSize: 13, fontWeight: 500,
            color: data.role === 'tutor' ? '#1d4ed8' : '#be185d',
        }}>
            {data.label}
        </span>
        <span {...removeProps} style={{
            cursor: 'pointer',
            color: data.role === 'tutor' ? '#1d4ed8' : '#be185d',
            fontWeight: 700, fontSize: 14, lineHeight: 1, padding: '0 2px',
        }}>
            ×
        </span>
    </div>
)

// ─── react-select styles ──────────────────────────────────────────────────────
const selectStyles = {
    control: (base, state) => ({
        ...base,
        borderColor: state.isFocused ? '#0d6efd' : '#dee2e6',
        boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(13,110,253,.15)' : 'none',
        borderRadius: '6px',
        minHeight: '42px',
        '&:hover': { borderColor: '#0d6efd' },
    }),
    option: () => ({}),
    multiValue: () => ({}),
    multiValueLabel: () => ({}),
    multiValueRemove: () => ({}),
    placeholder: (base) => ({ ...base, color: '#adb5bd', fontSize: '14px' }),
    noOptionsMessage: (base) => ({ ...base, fontSize: '14px', color: '#6c757d' }),
    loadingMessage: (base) => ({ ...base, fontSize: '14px', color: '#6c757d' }),
    menuList: (base) => ({ ...base, padding: 0 }),
}

// ─── Component ────────────────────────────────────────────────────────────────
const SendNotification = () => {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const [isSubmitting, setIsSubmitting] = useState(false)

    const {
        register,
        handleSubmit,
        control,
        formState: { errors },
        watch,
        reset,
        setValue,
        clearErrors,
    } = useForm({
        resolver: yupResolver(notificationSchema),
        defaultValues: {
            title: '',
            message: '',
            title_ar: '',
            message_ar: '',
            selectedUsers: [],
            sendAll: false,
            sendAllTutors: false,
            sendAllParents: false,
        },
    })

    const sendAll = watch('sendAll')
    const sendAllTutors = watch('sendAllTutors')
    const sendAllParents = watch('sendAllParents')
    const selectedUsers = watch('selectedUsers') ?? []

    // ─── loadOptions ──────────────────────────────────────────────────────────
    // When sendAllTutors is ON  → only show parents in dropdown (tutors are already all-selected)
    // When sendAllParents is ON → only show tutors in dropdown (parents are already all-selected)
    // When sendAll is ON        → dropdown is fully disabled, no load needed
    const loadOptions = useCallback(
        async (inputValue, _prevOptions, { page }) => {
            try {
                const { data } = await dispatch(
                    notificationOption({ search: inputValue, page, limit: 10 })
                ).unwrap()

                const tutors = data?.tutors ?? []
                const parents = data?.parents ?? []

                // Build option objects
                const tutorOptions = tutors.map((t) => ({
                    value: t.id,
                    label: t.username || `${t.first_name} ${t.last_name}`.trim(),
                    subLabel: t.username ? `${t.first_name} ${t.last_name}`.trim() : (t.email ?? ''),
                    role: 'tutor',
                }))

                const parentOptions = parents.map((p) => ({
                    value: p.id,
                    label: p.username || `${p.first_name} ${p.last_name}`.trim(),
                    subLabel: p.username ? `${p.first_name} ${p.last_name}`.trim() : (p.email ?? ''),
                    role: 'parent',
                }))

                // ── Key logic: filter out the "send all" group from the dropdown ──
                // If sendAllTutors is ON  → tutors are already covered, only show parents
                // If sendAllParents is ON → parents are already covered, only show tutors
                // If neither              → show both
                let filtered = []
                if (sendAllTutors && !sendAllParents) {
                    filtered = parentOptions                          // only parents selectable
                } else if (sendAllParents && !sendAllTutors) {
                    filtered = tutorOptions                           // only tutors selectable
                } else {
                    filtered = [...tutorOptions, ...parentOptions]    // show all
                }

                // Sort merged list alphabetically for seamless scroll
                filtered.sort((a, b) => a.label.localeCompare(b.label))

                // hasMore: only track groups that are still visible in the dropdown
                const tutorHasMore = page < (data?.tutor_pagination?.total_pages ?? 0)
                const parentHasMore = page < (data?.parent_pagination?.total_pages ?? 0)

                const hasMore = sendAllTutors
                    ? parentHasMore          // only tracking parents
                    : sendAllParents
                        ? tutorHasMore           // only tracking tutors
                        : tutorHasMore || parentHasMore

                return {
                    options: filtered,
                    hasMore,
                    additional: { page: page + 1 },
                }
            } catch (err) {
                console.error('Failed to load notification options:', err)
                return { options: [], hasMore: false, additional: { page: page + 1 } }
            }
        },
        // Re-run cache when sendAll flags change so dropdown re-filters immediately
        [dispatch, sendAllTutors, sendAllParents]
    )

    // ─── Checkbox handlers ────────────────────────────────────────────────────

    const handleSendAllChange = (e) => {
        const checked = e.target.checked
        setValue('sendAll', checked)
        if (checked) {
            setValue('sendAllTutors', false)
            setValue('sendAllParents', false)
            setValue('selectedUsers', [])   // clear specific selections — all users covered
        }
        clearErrors('selectedUsers')
    }

    const handleSendAllTutorsChange = (e) => {
        const checked = e.target.checked
        setValue('sendAllTutors', checked)

        if (checked) {
            setValue('sendAll', false)
            // Remove any already-selected tutors from dropdown selection
            // (they are now covered by "send all tutors")
            const withoutTutors = selectedUsers.filter((u) => u.role !== 'tutor')
            setValue('selectedUsers', withoutTutors)

            // If both groups are now "send all" → auto-upgrade to sendAll
            if (sendAllParents) {
                setValue('sendAll', true)
                setValue('sendAllTutors', false)
                setValue('sendAllParents', false)
                setValue('selectedUsers', [])
            }
        }
        clearErrors('selectedUsers')
    }

    const handleSendAllParentsChange = (e) => {
        const checked = e.target.checked
        setValue('sendAllParents', checked)

        if (checked) {
            setValue('sendAll', false)
            // Remove any already-selected parents from dropdown selection
            const withoutParents = selectedUsers.filter((u) => u.role !== 'parent')
            setValue('selectedUsers', withoutParents)

            // If both groups are now "send all" → auto-upgrade to sendAll
            if (sendAllTutors) {
                setValue('sendAll', true)
                setValue('sendAllTutors', false)
                setValue('sendAllParents', false)
                setValue('selectedUsers', [])
            }
        }
        clearErrors('selectedUsers')
    }

    // ─── Submit ───────────────────────────────────────────────────────────────
    // recipient_type tells the backend what to do:
    //   'all'      → backend fetches all active tutor + parent IDs itself
    //   'tutor'    → backend fetches all active tutor IDs + merges with user_ids (specific parents)
    //   'parent'   → backend fetches all active parent IDs + merges with user_ids (specific tutors)
    //   'specific' → only user_ids provided
    const onSubmit = async (data) => {
        setIsSubmitting(true)
        try {
            let recipientType = 'specific'
            let userIds = []

            if (data.sendAll) {
                recipientType = 'all'
                // No user_ids needed — backend handles fetching all IDs
            } else if (data.sendAllTutors && data.sendAllParents) {
                // Safety net: both checked at submit time → treat as all
                recipientType = 'all'
            } else if (data.sendAllTutors) {
                recipientType = 'tutor'
                // Send all tutors + any individually selected parents
                // Typed as { id, role } so backend knows which table each id belongs to
                userIds = (data.selectedUsers ?? [])
                    .filter((u) => u.role === 'parent')
                    .map((u) => ({ id: u.value, role: 'parent' }))
            } else if (data.sendAllParents) {
                recipientType = 'parent'
                // Send all parents + any individually selected tutors
                userIds = (data.selectedUsers ?? [])
                    .filter((u) => u.role === 'tutor')
                    .map((u) => ({ id: u.value, role: 'tutor' }))
            } else {
                recipientType = 'specific'
                // Both tutors and parents can be in the list — keep role on each
                userIds = (data.selectedUsers ?? []).map((u) => ({ id: u.value, role: u.role }))
            }

            const payload = {
                title: data.title,
                message: data.message,
                title_ar: data.title_ar,
                message_ar: data.message_ar,
                type: recipientType,
                // Only include user_ids when there are specific additions
                ...(userIds.length && { user_ids: userIds }),
            }

            await dispatch(addNotification(payload)).unwrap()

            Swal.fire({
                icon: 'success',
                title: 'Success!',
                text: 'Notification sent successfully',
                timer: 2000,
                showConfirmButton: false,
            })

            reset()
            setTimeout(() => navigate('/notification'), 2000)

        } catch (error) {
            console.error(error)
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error?.message || 'Failed to send notification',
            })
        } finally {
            setIsSubmitting(false)
        }
    }

    // Dropdown fully disabled only when "Send All" covers everyone
    const isSelectDisabled = sendAll

    // ─── Render ───────────────────────────────────────────────────────────────
    return (
        <div className="container-fluid px-4 py-3">

            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="fw-semibold mb-0">Send Notification</h4>
                <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => navigate('/notification')}
                    style={{ padding: '8px 20px', fontSize: '14px', borderRadius: '6px' }}
                >
                    ← Back to List
                </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '8px' }}>
                    <div className="card-body p-4">
                        <div className="row g-4">

                            {/* ── Select Users ── */}
                            <div className="col-12">
                                <label className="form-label fw-medium">Select Users</label>

                                {/* Hint text so user knows what the dropdown shows */}
                                {(sendAllTutors || sendAllParents) && !sendAll && (
                                    <div className="mb-1" style={{ fontSize: 12, color: '#6c757d' }}>
                                        {sendAllTutors && !sendAllParents &&
                                            'All tutors selected — you can add specific parents below'}
                                        {sendAllParents && !sendAllTutors &&
                                            'All parents selected — you can add specific tutors below'}
                                    </div>
                                )}

                                <Controller
                                    name="selectedUsers"
                                    control={control}
                                    render={({ field }) => (
                                        <AsyncPaginate
                                            {...field}
                                            isMulti
                                            isDisabled={isSelectDisabled}
                                            loadOptions={loadOptions}
                                            additional={{ page: 1 }}
                                            debounceTimeout={400}
                                            placeholder={
                                                sendAll ? 'All users will receive this notification' :
                                                    sendAllTutors ? 'Search parents to add…' :
                                                        sendAllParents ? 'Search tutors to add…' :
                                                            'Search users…'
                                            }
                                            styles={selectStyles}
                                            components={{
                                                Option: CustomOption,
                                                MultiValue: CustomMultiValue,
                                                MultiValueLabel: () => null,
                                                MultiValueRemove: () => null,
                                            }}
                                            onChange={(val) => {
                                                field.onChange(val)
                                                clearErrors('selectedUsers')
                                            }}
                                            // Bust cache whenever send-all flags change
                                            // so the dropdown re-filters the visible options
                                            cacheUniqs={[sendAll, sendAllTutors, sendAllParents]}
                                        />
                                    )}
                                />
                                {errors.selectedUsers && (
                                    <div className="text-danger mt-1" style={{ fontSize: '13px' }}>
                                        {errors.selectedUsers.message}
                                    </div>
                                )}
                            </div>

                            {/* ── Title ── */}
                            <div className="col-md-6">
                                <label className="form-label fw-medium">
                                    Title <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="text"
                                    className={`form-control ${errors.title ? 'is-invalid' : ''}`}
                                    placeholder="Enter title"
                                    {...register('title')}
                                />
                                {errors.title && (
                                    <div className="invalid-feedback">{errors.title.message}</div>
                                )}
                            </div>

                            {/* ── Message ── */}
                            <div className="col-md-6">
                                <label className="form-label fw-medium">
                                    Message <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="text"
                                    className={`form-control ${errors.message ? 'is-invalid' : ''}`}
                                    placeholder="Enter message"
                                    {...register('message')}
                                />
                                {errors.message && (
                                    <div className="invalid-feedback">{errors.message.message}</div>
                                )}
                            </div>

                            {/* ── Title Arabic── */}
                            <div className="col-md-6">
                                <label className="form-label fw-medium">
                                    Title (Arabic) <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="text"
                                    className={`form-control ${errors.title_ar ? 'is-invalid' : ''}`}
                                    placeholder="Enter title (Arabic)"
                                    {...register('title_ar')}
                                />
                                {errors.title_ar && (
                                    <div className="invalid-feedback">{errors.title_ar.message}</div>
                                )}
                            </div>

                            {/* ── Message Arabic── */}
                            <div className="col-md-6">
                                <label className="form-label fw-medium">
                                    Message (Arabic) <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="text"
                                    className={`form-control ${errors.message_ar ? 'is-invalid' : ''}`}
                                    placeholder="Enter message (Arabic)"
                                    {...register('message_ar')}
                                />
                                {errors.message_ar && (
                                    <div className="invalid-feedback">{errors.message_ar.message}</div>
                                )}
                            </div>

                            {/* ── Checkboxes ── */}
                            <div className="col-12 d-flex flex-wrap gap-4 align-items-center">

                                <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="sendAll"
                                        {...register('sendAll')}
                                        onChange={handleSendAllChange}
                                    />
                                    <label className="form-check-label" htmlFor="sendAll">
                                        Send All
                                    </label>
                                </div>

                                <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="sendAllTutors"
                                        disabled={sendAll}
                                        {...register('sendAllTutors')}
                                        onChange={handleSendAllTutorsChange}
                                    />
                                    <label
                                        className={`form-check-label ${sendAll ? 'text-muted' : ''}`}
                                        htmlFor="sendAllTutors"
                                    >
                                        Send All Tutors
                                    </label>
                                </div>

                                <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="sendAllParents"
                                        disabled={sendAll}
                                        {...register('sendAllParents')}
                                        onChange={handleSendAllParentsChange}
                                    />
                                    <label
                                        className={`form-check-label ${sendAll ? 'text-muted' : ''}`}
                                        htmlFor="sendAllParents"
                                    >
                                        Send All Parents
                                    </label>
                                </div>

                            </div>

                        </div>
                    </div>
                </div>

                {/* ── Form Actions ── */}
                <div className="d-flex gap-3 justify-content-end">
                    <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => navigate('/notification')}
                        disabled={isSubmitting}
                        style={{ padding: '10px 24px', fontSize: '14px', borderRadius: '6px' }}
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="btn btn-success"
                        disabled={isSubmitting}
                        style={{ padding: '10px 24px', fontSize: '14px', borderRadius: '6px', minWidth: '120px' }}
                    >
                        {isSubmitting ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                                Sending…
                            </>
                        ) : 'Submit'}
                    </button>
                </div>
            </form>
        </div>
    )
}

export default SendNotification